import type { ChatSource } from '@cruz/shared';

const ZONE = 'Europe/Madrid';

/** What the user sees while a tool runs. */
export function toolLabel(name: string): string {
  switch (name) {
    case 'find_brotherhood':
      return 'Buscando la hermandad…';
    case 'brotherhood_info':
      return 'Consultando los datos de la hermandad…';
    case 'brotherhood_schedule':
    case 'brotherhoods_of_day':
      return 'Consultando el horario oficial…';
    case 'itinerary':
      return 'Consultando el recorrido…';
    case 'passing_time':
    case 'what_passes_by':
      return 'Calculando las horas de paso…';
    case 'day_status':
      return 'Mirando quién está en la calle…';
    default:
      return 'Consultando los datos…';
  }
}

const updatedFormat = new Intl.DateTimeFormat('es-ES', {
  timeZone: ZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export interface SourceLine {
  /** Short, discreet: "Ayuntamiento de Sevilla · datos de 2026 · act. 9 oct 2026, 10:06" */
  text: string;
  /** Full source names, for a tooltip */
  detail: string;
}

/**
 * Discreet lines under an answer: who publishes the data, which year and when it was
 * updated. Another year's schedule is always flagged as a reference. Sources from the
 * same publisher and year share one line.
 */
export function sourceLines(sources: ChatSource[]): SourceLine[] {
  const lines = new Map<string, { text: string; details: Set<string> }>();
  for (const s of sources) {
    const parts = [s.source.split(',')[0]!.trim()];
    if (s.year != null) {
      parts.push(s.is_reference ? `datos de ${s.year} (referencia)` : `datos de ${s.year}`);
    }
    const key = parts.join(' · ');
    const line = lines.get(key) ?? { text: key, details: new Set<string>() };
    if (s.updated_at && !line.text.includes('actualizado')) {
      line.text += ` · actualizado el ${updatedFormat.format(new Date(s.updated_at))}`;
    }
    line.details.add(s.source);
    lines.set(key, line);
  }
  return [...lines.values()].map((l) => ({ text: l.text, detail: [...l.details].join('; ') }));
}

/** Hour of the day in Seville for an instant. */
export function sevilleHour(date: Date): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: ZONE, hour: '2-digit', hourCycle: 'h23' }).format(
      date,
    ),
  );
}

const clockFormat = new Intl.DateTimeFormat('es-ES', {
  timeZone: ZONE,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const formatClock = (date: Date): string => clockFormat.format(date);
